// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";

// CSS
import "./DocumentGenerationOptionsDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// // API IMPORTS
import {
	putAPICall,
	postAPICall,
	postFormDataAPICall,
} from "../../../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../../../context/UserContext";

// MUI COMPONENTS
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import Button from "@mui/material/Button";

import TextField from "@mui/material/TextField";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LinearProgress, Typography } from "@mui/material";

// MUI ICONS

// CUSTOM COMPONENTS

// CONSTANTS

const titleMap = {
	buildingEnvelopeRepairs: "Building Envelope Repairs",
	roofRepairs: "Roof Repairs",
	custom: "Custom",
};
const subtitleMap = {
	bidSet: "Bid Set",
	constructionSet: "Construction Set",
	permitSet: "Permit Set",
	preliminaryReviewSet: "Preliminary Review Set",
	custom: "Custom",
};
const TOC_LAYOUT = {
	pageHeight: 792,
	topMargin: 31,
	linkXStart: 25,
	linkXEnd: 500,
	rowHeight: 16,
	rowStartOffset: 38,
	maxRowsPerPage: 42,
};

// OTHER
// To convert the date to UTC
// var utc = require("dayjs/plugin/utc");
// dayjs.extend(utc);

// REACT COMPONENT
const DocumentGenerationOptionsDialog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	// Form Data
	const [publishDate, setPublishDate] = useState(null);

	const [reportTitleSelect, setReportTitleSelect] = useState("");
	const [reportTitleText, setReportTitleText] = useState("");

	const [reportSubtitleSelect, setReportSubtitleSelect] = useState("");
	const [reportSubtitleText, setReportSubtitleText] = useState("");
	const [documentAddress, setDocumentAddress] = useState("");
	const [isDocumentAddressEdited, setIsDocumentAddressEdited] =
		useState(false);
	const [propertyName, setPropertyName] = useState("");
	const [isPropertyNameEdited, setIsPropertyNameEdited] = useState(false);

	// Feedback
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	const [isLoading, setIsLoading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [progressMessage, setProgressMessage] = useState("");

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		if (props.open) {
			if (!isDocumentAddressEdited) {
				setDocumentAddress(buildDefaultDocumentAddress());
			}
			if (!isPropertyNameEdited) {
				setPropertyName(props.job?.property_name || "");
			}
		}
	}, [props.open, props.propertyDetails, props.job, isDocumentAddressEdited, isPropertyNameEdited]);

	// INPUT HANDLERS
	// Form Data Handlers
	const publishDateChanged = (newValue) => {
		setPublishDate(newValue || dayjs());
	};

	const reportTitleSelectChanged = (event) => {
		setReportTitleSelect(event.target.value);
		setReportSubtitleText("");
	};

	const reportTitleTextChanged = (event) => {
		setReportTitleText(event.target.value);
	};

	const reportSubtitleSelectChanged = (event) => {
		setReportSubtitleSelect(event.target.value);
		setReportSubtitleText("");
	};

	const reportSubtitleTextChanged = (event) => {
		setReportSubtitleText(event.target.value);
	};

	const documentAddressChanged = (event) => {
		setDocumentAddress(event.target.value);
		setIsDocumentAddressEdited(true);
	};

	const propertyNameChanged = (event) => {
		setPropertyName(event.target.value);
		setIsPropertyNameEdited(true);
	};

	// Button Handlers
	const submitButtonClicked = async () => {
		if (!validateInputFields()) {
			return;
		}
		setProgress(0); // this is the percentage
		setProgressMessage("Generating...");

		setIsLoading(true);
		let pdfIndividualSpecUrls = [];

		try {
			// Determine the correct title and subtitle based on the selected or custom values
			const finalTitle =
				reportTitleSelect === "custom"
					? reportTitleText
					: titleMap[reportTitleSelect];
			const finalSubtitle =
				reportSubtitleSelect === "custom"
					? reportSubtitleText
					: subtitleMap[reportSubtitleSelect];

			if (props.generateCoverPage === true) {
				// Create Cover Page:
				setProgressMessage("Creating Cover Page...");
				setProgress(20);

				const coverPageUrl = await createCoverPage(
					finalTitle,
					finalSubtitle,
				);
				pdfIndividualSpecUrls.push(coverPageUrl);
			}

			setProgressMessage(
				props.selectedSpecifications.length > 1
					? "Preparing Specs..."
					: "Preparing Spec...",
			);
			setProgress(40);

			let specPageStartMap = new Map();
			if (props.generateTableOfContents === true) {
				specPageStartMap = await getSpecPageStartMap(
					finalTitle,
					finalSubtitle,
				);
			}

			setProgressMessage("Creating Document...");
			setProgress(60);
			const combinedBody = await createCombinedSpecsPdf(
				finalTitle,
				finalSubtitle,
				{
					specPageStartMap,
					coverPageCount: props.generateCoverPage ? 1 : 0,
				},
			);
			pdfIndividualSpecUrls.push(...combinedBody.pdfUrls);

			setProgressMessage("Merging...");
			setProgress(80);

			// Merge PDFS:
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/convert/merge-pdf",
				{
					files: pdfIndividualSpecUrls,
				},
			);

			let finalPdfBlob = null;
			let finalPdfUrl = res.data.mergedPdfUrl;
			if (
				props.generateTableOfContents === true &&
				combinedBody.tocLinks.length > 0
			) {
				try {
					setProgressMessage("Adding TOC Links...");
					setProgress(90);
					const annotationRes = await postAPICall(
						instance,
						accounts[0],
						"/api/convert/annotate-specbook-toc-links",
						{
							pdfUrl: res.data.mergedPdfUrl,
							links: combinedBody.tocLinks,
							layout: TOC_LAYOUT,
						},
					);
					finalPdfBlob = base64ToPdfBlob(
						annotationRes.data.pdfBase64,
					);
					finalPdfUrl = URL.createObjectURL(finalPdfBlob);
				} catch (annotationError) {
					console.log(
						"Error adding TOC links, falling back to merged PDF:",
						annotationError,
					);
				}
			}

			const generatedFileName = buildGeneratedFileName(finalTitle);

			// Auto-download generated PDF (restores prior behavior even when using blob URLs).
			if (finalPdfUrl) {
				try {
					if (!finalPdfBlob) {
						finalPdfBlob = await fetch(finalPdfUrl).then((r) =>
							r.blob(),
						);
					}
					triggerPdfDownload(finalPdfBlob, generatedFileName);
				} catch (downloadError) {
					console.log(
						"Error auto-downloading PDF, opening in new tab:",
						downloadError,
					);
					window.open(finalPdfUrl, "_blank");
				}
			}

			if (props.saveToS3 === true) {
				// create form data to send
				const formData = new FormData();
				formData.append("title", finalTitle);
				formData.append("subtitle", finalSubtitle);
				formData.append("phaseID", props.jobID);
				formData.append("modifiedBy", employeeID);

				const fileBlob =
					finalPdfBlob ||
					(await fetch(finalPdfUrl).then((r) => {
						return r.blob();
					}));
				// title + date in this format: "Bid_Specs_24_08_2022_1"
				let fileName = generatedFileName;
				formData.append("file", fileBlob, fileName);

				// save the generated pdf to the database
				const saveRes = await postFormDataAPICall(
					instance,
					accounts[0],
					"/api/specifications/addSpecBook",
					formData,
				);
			}

			if (!props.singleSpec) {
				setProgressMessage("Locking Job...");
				setProgress(95);
				// Lock the job
				await putAPICall(
					instance,
					accounts[0],
					"/api/jobs/design/toggleLock",
					{
						id: props.jobID,
						isLocked: 1,
						employeeID: employeeID,
					},
				);

				// Create Local Copies
				await props.createLocalCopies();
			}
		} catch (error) {
			console.log("Error in submitButtonClicked: ", error);
		}

		setProgressMessage("Cleaning Up...");
		setProgress(100);
		props.fetchDesignJobRecord();
		setIsLoading(false);

		formClosed();
	};

	// Create Cover Page:
	const createCoverPage = async (finalTitle, finalSubtitle) => {
		const address = getResolvedDocumentAddress();

		let coverSheetHTML = `<div class="documentContainer">`;
		coverSheetHTML += `<div class="headerContainer"><div class="headerContainer__titleContainer"><div class="headerContainer__title">${getResolvedPropertyName()}</div><div class="headerContainer__subtitle">${finalSubtitle}</div></div></div>`;
		coverSheetHTML += `<div class="photoContainer"></div><div class="logoContainer"></div>`;
		coverSheetHTML += `<div class="documentInfoContainer"><div class="documentInfoContainer__titleContainer"><div class="documentInfoContainer__titleContainer__title">${finalTitle}</div><div class="documentInfoContainer__titleContainer__subtitle">${
			getResolvedPropertyName()
		}</div><div class="documentInfoContainer__titleContainer__subtitle">${address}</div><div class="documentInfoContainer__titleContainer__subtitle">${publishDate
			.startOf("day")
			.format("MM/DD/YYYY")}</div></div></div>`;
		coverSheetHTML += `<div class="footerContainer"> <div class="footerContainer_top"></div> <div class="footerContainer_middle"> <div class="footerContainer_middle__row"> <div class="footerContainer_middle__row__column"> <div class="footerContainer_middle__row__column__state">WA</div> <div class="footerContainer_middle__row__column__address">12125 Harbour Reach Drive, Ste 200<br> Mukilteo, WA 98275</div> </div> <div class="footerContainer_middle__row__column"> <div class="footerContainer_middle__row__column__state">OR</div> <div class="footerContainer_middle__row__column__address">4800 S Macadam Ave Ste. 305<br>Portland, OR 97239</div> </div> <div class="footerContainer_middle__row__column"> <div class="footerContainer_middle__row__column__state">UT</div> <div class="footerContainer_middle__row__column__address">740 E 3900 South Ste. 208<br>Salt Lake City, UT 84107</div> </div> </div> <div class="footerContainer_middle__row"> <div class="footerContainer_middle__row__column"> <div class="footerContainer_middle__row__column__icon" id="email"></div> <div class="footerContainer_middle__row__column__detail">HELLO@J2CONSULTANTS.COM</div> </div> <div class="footerContainer_middle__row__column"> <div class="footerContainer_middle__row__column__icon" id="phone"></div> <div class="footerContainer_middle__row__column__detail">(425) 774-5600</div> </div> <div class="footerContainer_middle__row__column"> <div class="footerContainer_middle__row__column__icon" id="web"></div> <div class="footerContainer_middle__row__column__detail">WWW.J2CONSULTANTS.COM</div> </div> </div> </div> <div class="footerContainer_bottom"> <div class="footerContainer_bottom__textContainer"> <p>J2 Building Consultants</p><p class="pVertBar">|</p><p>The Building Doctors</p> </div> </div> </div> </div>`;

		try {
			const coverPageRes = await postAPICall(
				instance,
				accounts[0],
				"/api/convert/coverSheetPDF",
				{
					title: finalTitle,
					subtitle: finalSubtitle,
					htmlContent: coverSheetHTML,
					include_footer: false,
					include_header: false,
				},
			);

			return coverPageRes.data.Files[0].Url;
		} catch (error) {
			console.log("Error in createCoverPage: ", error);
			return "";
		}
	};

	const createTableOfContentsHTML = ({
		specPageStartMap = new Map(),
		specStartOffset = 0,
	}) => {
		let divisionMap = new Map();

		// Group specifications by division
		props.selectedSpecifications.forEach((spec, index) => {
			const division = spec.division ? spec.division.split(" ")[0] : "";
			if (division) {
				if (!divisionMap.has(division)) {
					divisionMap.set(division, []);
				}
				divisionMap.get(division).push({
					spec,
					specKey: getSpecKey(spec, index),
				});
			}
		});

		// generate the HTML for each division, but only when a division has specs
		const filteredDivisionCodes = props.divisionCodes.filter((code) =>
			divisionMap.has(String(code.division_code).trim()),
		);

		const rows = [];
		filteredDivisionCodes.forEach((code) => {
			const divisionCode = String(code.division_code).trim();
			rows.push({
				type: "division",
				text: `DIVISION ${divisionCode}&nbsp;&nbsp;&nbsp;${code.name.toUpperCase()}`,
			});
			(divisionMap.get(divisionCode) || []).forEach(
				({ spec, specKey }) => {
					const specPageInSpecs = specPageStartMap.get(specKey) || 1;
					rows.push({
						type: "spec",
						division: spec.division || "",
						title: spec.title || "",
						targetPage: specStartOffset + specPageInSpecs,
					});
				},
			);
		});

		const tocPages = [];
		for (let i = 0; i < rows.length; i += TOC_LAYOUT.maxRowsPerPage) {
			tocPages.push(rows.slice(i, i + TOC_LAYOUT.maxRowsPerPage));
		}
		if (tocPages.length === 0) {
			tocPages.push([]);
		}

		const tocLinks = [];
		const styleTag = `<style>
			.toc-page { font-family: Arial, sans-serif; font-size: 12px; }
			.toc-section-title { text-align: center; text-transform: uppercase; font-weight: bold; line-height: 14px; height: 14px; margin: 0; }
			.toc-main-title { text-align: center; text-transform: uppercase; font-weight: bold; line-height: 14px; height: 14px; margin: 0 0 10px 0; }
			.toc-row-spec-link,
			.toc-row-spec-link:link,
			.toc-row-spec-link:visited,
			.toc-row-spec-link:hover,
			.toc-row-spec-link:active { color: #184c93; text-decoration: none; }
		</style>`;

		const pagesHtml = tocPages
			.map((pageRows, pageIndex) => {
				const rowsHtml = pageRows
					.map((row, rowIndex) => {
						if (row.type === "division") {
							return `<h1 style="text-align: left;">${row.text}</h1>`;
						}

						const linkId = tocLinks.length;
						tocLinks.push({
							tocPageInSection: pageIndex + 1,
							rowIndex,
							linkId,
							targetPage: row.targetPage,
						});

						return `<p><a href="https://j2.local/toc-link-${linkId}" class="toc-row-spec-link">${row.division}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${row.title}</a></p>`;
					})
					.join("");

				const pageBreak =
					pageIndex < tocPages.length - 1
						? '<div style="page-break-before: always;"></div>'
						: "";

				return `<div class="toc-page"><div class="toc-section-title">SECTION 00 01 10</div><div class="toc-main-title">TABLE OF CONTENTS</div>${rowsHtml}</div>${pageBreak}`;
			})
			.join("");

		return {
			html: `${styleTag}${pagesHtml}`,
			tocLinks,
			pageCount: Math.max(tocPages.length, 1),
		};
	};

	const convertHtmlBlockToPdfUrl = async (
		htmlContent,
		finalTitle,
		finalSubtitle,
		{
			specTitle = finalTitle,
			specSubtitle = finalSubtitle,
			includeHeader = true,
			includeFooter = true,
		} = {},
	) => {
		const conversionRes = await postAPICall(
			instance,
			accounts[0],
			"/api/convert/html-to-pdf",
			{
				htmlContent,
				property: getResolvedPropertyName(),
				address: getResolvedDocumentAddress(),
				date: publishDate.startOf("day").format("MM/DD/YYYY"),
				subtitle: finalSubtitle,
				title: finalTitle,
				spec_subtitle: specSubtitle,
				spec_title: specTitle,
				include_footer: includeFooter,
				include_header: includeHeader,
			},
		);
		return conversionRes.data.Files[0].Url;
	};

	const createSpecPdfUrls = async (finalTitle, finalSubtitle) =>
		Promise.all(
			props.selectedSpecifications.map(async (spec, index) => {
				const specKey = getSpecKey(spec, index);
				const specAnchor = getSpecAnchorTagFromKey(specKey);
				const specHtml = `<div class="page" style="counter-reset: part-header section-header subsection subsection-list subsection-list-details subSubsection-list-details;">${specAnchor}${spec.content}</div>`;

				return convertHtmlBlockToPdfUrl(specHtml, finalTitle, finalSubtitle, {
					specTitle: formatSpecSectionHeader(spec.division),
					specSubtitle: spec.title,
					includeHeader: true,
					includeFooter: true,
				});
			}),
		);

	const createCombinedSpecsPdf = async (
		finalTitle,
		finalSubtitle,
		{ specPageStartMap = new Map(), coverPageCount = 0 } = {},
	) => {
		const pdfUrls = [];
		let tocLinks = [];
		if (props.generateTableOfContents === true) {
			const initialTOC = createTableOfContentsHTML({
				specPageStartMap,
				specStartOffset: 0,
			});

			const finalTOC = createTableOfContentsHTML({
				specPageStartMap,
				specStartOffset: coverPageCount + initialTOC.pageCount,
			});
			tocLinks = finalTOC.tocLinks.map((link) => ({
				tocPage: coverPageCount + link.tocPageInSection,
				rowIndex: link.rowIndex,
				linkId: link.linkId,
				targetPage: link.targetPage,
			}));
			const tocPdfUrl = await convertHtmlBlockToPdfUrl(
				finalTOC.html,
				finalTitle,
				finalSubtitle,
				{
					specTitle: "SECTION 00 01 10",
					specSubtitle: "TABLE OF CONTENTS",
					includeHeader: true,
					includeFooter: true,
				},
			);
			pdfUrls.push(tocPdfUrl);
		}

		const specPdfUrls = await createSpecPdfUrls(finalTitle, finalSubtitle);
		pdfUrls.push(...specPdfUrls);

		return { pdfUrls, tocLinks };
	};

	const getSpecKey = (spec, fallbackIndex = 0) =>
		spec?.id ??
		spec?.spec_id ??
		`${spec?.division || ""}::${spec?.title || ""}::${fallbackIndex}`;

	const getSpecAnchorId = (specKey) =>
		`spec-anchor-${String(specKey).replace(/[^a-zA-Z0-9_-]/g, "_")}`;

	const getSpecAnchorTagFromKey = (specKey) => {
		const anchorId = getSpecAnchorId(specKey);
		return `<a id="${anchorId}" name="${anchorId}"></a>`;
	};

	const formatSpecSectionHeader = (division) => {
		const trimmedDivision = String(division || "").trim();
		const digitsOnly = trimmedDivision.replace(/\D/g, "");

		if (digitsOnly.length === 6) {
			return `SECTION ${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 6)}`;
		}

		if (/^\d{2}\s+\d{2}\s+\d{2}$/.test(trimmedDivision)) {
			return `SECTION ${trimmedDivision}`;
		}

		return `SECTION ${trimmedDivision}`;
	};

	const getSpecPageStartMap = async (finalTitle, finalSubtitle) => {
		const specUrls = await Promise.all(
			props.selectedSpecifications.map(async (spec) => {
				const tempSelectedSpecsRes = await postAPICall(
					instance,
					accounts[0],
					"/api/convert/html-to-pdf",
					{
						htmlContent: spec.content,
						property: getResolvedPropertyName(),
						address: getResolvedDocumentAddress(),
						date: publishDate.startOf("day").format("MM/DD/YYYY"),
						subtitle: finalSubtitle,
						title: finalTitle,
						spec_title: formatSpecSectionHeader(spec.division),
						spec_subtitle: spec.title,
						include_footer: true,
						include_header: true,
					},
				);
				return tempSelectedSpecsRes.data.Files[0].Url;
			}),
		);

		const specPageCounts = await Promise.all(
			specUrls.map(async (url) => getPdfPageCount(url)),
		);

		const specStartMap = new Map();
		let runningPage = 0;
		props.selectedSpecifications.forEach((spec, index) => {
			specStartMap.set(getSpecKey(spec, index), runningPage + 1);
			runningPage += specPageCounts[index] || 1;
		});
		return specStartMap;
	};

	// Lightweight page count parser used only for TOC page-target offsets.
	const getPdfPageCount = async (pdfUrl) => {
		try {
			const response = await fetch(pdfUrl);
			const buffer = await response.arrayBuffer();
			const pdfText = new TextDecoder("latin1").decode(buffer);
			const matches = pdfText.match(/\/Type\s*\/Page\b/g);
			return matches ? matches.length : 1;
		} catch (error) {
			console.log("Error in getPdfPageCount: ", error);
			return 1;
		}
	};

	const base64ToPdfBlob = (base64Data) => {
		const binaryString = window.atob(base64Data);
		const bytes = new Uint8Array(binaryString.length);
		for (let i = 0; i < binaryString.length; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}
		return new Blob([bytes], { type: "application/pdf" });
	};

	const buildGeneratedFileName = (finalTitle) =>
		`${finalTitle.replace(/\s+/g, "_")}_${publishDate
			.startOf("day")
			.format("MM_DD_YYYY")}_${props.jobID}.pdf`;

	const triggerPdfDownload = (blob, fileName) => {
		const objectUrl = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = objectUrl;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(objectUrl);
	};

	// Feedback Handlers
	const openAlert = (message) => {
		setAlertOpen(true);
		setAlertMessage(message);
	};

	const closeAlert = () => {
		setAlertOpen(false);
		setAlertMessage("");
	};

	// API FUNCTIONS

	// HELPER FUNCTIONS
	const buildDefaultDocumentAddress = () => {
		const addressParts = [
			props.propertyDetails.address_line1,
			props.propertyDetails.address_line2,
		].filter(Boolean);
		const localityParts = [
			props.propertyDetails.city,
			props.propertyDetails.state,
		].filter(Boolean);
		const zip = props.propertyDetails.zip ? ` ${props.propertyDetails.zip}` : "";
		const locality = localityParts.length
			? `${localityParts.join(", ")}${zip}`
			: props.propertyDetails.zip || "";

		return [...addressParts, locality].filter(Boolean).join(", ");
	};

	const getResolvedDocumentAddress = () => documentAddress.trim();

	const getResolvedPropertyName = () => propertyName.trim();

	const resetForm = () => {
		setPublishDate(null);

		setReportTitleSelect("");
		setReportTitleText("");

		setReportSubtitleSelect("");
		setReportSubtitleText("");
		setDocumentAddress(buildDefaultDocumentAddress());
		setIsDocumentAddressEdited(false);
		setPropertyName(props.job?.property_name || "");
		setIsPropertyNameEdited(false);

		closeAlert();
	};

	const validateInputFields = () => {
		if (!publishDate) {
			openAlert("Please select a publish date.");
			return false;
		}

		if (propertyName.trim() === "") {
			openAlert("Please enter a property name.");
			return false;
		}

		if (documentAddress.trim() === "") {
			openAlert("Please enter a document address.");
			return false;
		}

		if (reportTitleSelect === "") {
			openAlert("Please select a report title.");
			return false;
		}

		if (reportTitleSelect === "custom" && reportTitleText === "") {
			openAlert("Please enter a custom report title.");
			return false;
		}

		if (reportSubtitleSelect === "") {
			openAlert("Please select a report subtitle.");
			return false;
		}

		if (reportSubtitleSelect === "custom" && reportSubtitleText === "") {
			openAlert("Please enter a custom report subtitle.");
			return false;
		}

		return true;
	};

	const formClosed = () => {
		resetForm();
		props.documentGenerationOptionsDialogClosed();
	};

	// RENDER
	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Dialog
				id="documentGenerationOptionsDialog"
				open={props.open}
				fullWidth
				disableEscapeKeyDown
			>
				<DialogTitle id="documentGenerationOptionsDialogTitle">
					Generate Document
				</DialogTitle>
				<DialogContent id="documentGenerationOptionsDialogContent">
					{isLoading && (
						<div className="loadingBarContainer">
							<LinearProgress
								variant="determinate"
								value={progress}
							/>
							<Typography>{progressMessage}</Typography>
						</div>
					)}
					{!isLoading && (
						<>
							<Collapse
								sx={{ width: "stretch" }}
								id="documentGenerationOptionsDialogCollapse"
								in={alertOpen}
							>
								<div className="documentGenerationOptionsDialogAlertContainer">
									<Alert
										sx={{ width: "stretch" }}
										id="documentGenerationOptionsDialogAlert"
										severity="error"
										action={
											<IconButton
												id="documentGenerationOptionsDialogCloseIconButton"
												aria-label="close"
												color="inherit"
												size="small"
												onClick={closeAlert}
											>
												<CloseIcon
													id="documentGenerationOptionsDialogAlertCloseIcon"
													fontSize="inherit"
												/>
											</IconButton>
										}
									>
										{alertMessage}
									</Alert>
								</div>
							</Collapse>
							<div className="documentGenerationOptionsDialogSectionFormData">
								<div className="documentGenerationOptionsDialogSection">
									<div className="documentGenerationOptionsDialogRow">
										<DatePicker
											sx={{ flex: 1 }}
											label="Publish Date"
											value={publishDate}
											onChange={publishDateChanged}
											slotProps={{
												textField: { size: "medium" },
											}}
										/>
									</div>
								</div>
								<div className="documentGenerationOptionsDialogSection">
									<div className="documentGenerationOptionsDialogRow">
										<FormControl sx={{ flex: 1 }}>
											<InputLabel id="titleLabel">
												Report Title
											</InputLabel>
											<Select
												labelId="titleLabel"
												id="title"
												value={reportTitleSelect}
												label="Report Title"
												onChange={
													reportTitleSelectChanged
												}
											>
												<MenuItem
													key={1}
													value="buildingEnvelopeRepairs"
												>
													Building Envelope Repairs
												</MenuItem>
												<MenuItem
													key={2}
													value="roofRepairs"
												>
													Roof Repairs
												</MenuItem>
												<MenuItem
													key={3}
													value="custom"
												>
													Custom
												</MenuItem>
											</Select>
										</FormControl>
									</div>
									<div className="documentGenerationOptionsDialogRow">
										<Collapse
											sx={{ flex: 1 }}
											in={reportTitleSelect === "custom"}
										>
											<div className="documentGenerationOptionsDialogCustomTitleContainer">
												<TextField
													sx={{ flex: 1 }}
													id="customTitle"
													label="Custom Title"
													value={reportTitleText}
													onChange={
														reportTitleTextChanged
													}
												/>
											</div>
										</Collapse>
									</div>
								</div>
								<div className="documentGenerationOptionsDialogSection">
									<div className="documentGenerationOptionsDialogRow">
										<FormControl sx={{ flex: 1 }}>
											<InputLabel id="subtitleLabel">
												Report Subtitle
											</InputLabel>
											<Select
												labelId="subtitleLabel"
												id="subtitle"
												value={reportSubtitleSelect}
												label="Report Subtitle"
												onChange={
													reportSubtitleSelectChanged
												}
											>
												<MenuItem
													key={1}
													value="bidSet"
												>
													Bid Set
												</MenuItem>
												<MenuItem
													key={2}
													value="constructionSet"
												>
													Construction Set
												</MenuItem>
												<MenuItem
													key={3}
													value="permitSet"
												>
													Permit Set
												</MenuItem>
												<MenuItem
													key={4}
													value="preliminaryReviewSet"
												>
													Preliminary Review Set
												</MenuItem>
												<MenuItem
													key={5}
													value="custom"
												>
													Custom
												</MenuItem>
											</Select>
										</FormControl>
									</div>
									<div className="documentGenerationOptionsDialogRow">
										<Collapse
											sx={{ flex: 1 }}
											in={
												reportSubtitleSelect ===
												"custom"
											}
										>
											<div className="documentGenerationOptionsDialogCustomTitleContainer">
												<TextField
													sx={{ flex: 1 }}
													id="customSubtitle"
													label="Custom Subtitle"
													value={reportSubtitleText}
													onChange={
														reportSubtitleTextChanged
													}
												/>
											</div>
										</Collapse>
									</div>
								</div>
								<div className="documentGenerationOptionsDialogSection">
									<div className="documentGenerationOptionsDialogRow">
										<TextField
											sx={{ flex: 1 }}
											id="propertyName"
											label="Property Name"
											value={propertyName}
											onChange={propertyNameChanged}
											helperText="Prefilled from the property record. Edit it to override the property name on the generated document."
										/>
									</div>
								</div>
								<div className="documentGenerationOptionsDialogSection">
									<div className="documentGenerationOptionsDialogRow">
										<TextField
											sx={{ flex: 1 }}
											id="documentAddress"
											label="Document Address"
											value={documentAddress}
											onChange={documentAddressChanged}
											helperText="Prefilled from the property record. Edit it to override the generated document address."
										/>
									</div>
								</div>
							</div>
						</>
					)}
					{!isLoading ? (
						<div className="documentGenerationOptionsDialogButtons">
							<Button
								onClick={formClosed}
								variant="outlined"
								color="error"
							>
								Cancel
							</Button>
							<Button
								onClick={submitButtonClicked}
								variant="contained"
							>
								Submit
							</Button>
						</div>
					) : null}
				</DialogContent>
			</Dialog>
		</LocalizationProvider>
	);
};

// EXPORT
export default DocumentGenerationOptionsDialog;
